import { useTranslation } from 'react-i18next';
import PageHero from '../../components/shared/PageHero';
import Sidebar from '../../components/shared/Sidebar';
import { Heart, GraduationCap, BookOpen, Lightbulb, ArrowRight, Wallet } from 'lucide-react';

export default function Donate() {
  const { t } = useTranslation();

  const impactAreas = [
    { icon: <GraduationCap className="w-8 h-8" />, titleKey: 'donate_impact_scholarships', desc: 'Help talented students access quality education regardless of financial circumstances.' },
    { icon: <BookOpen className="w-8 h-8" />, titleKey: 'donate_impact_library', desc: 'Fund new books, journals, and digital resources for our learning community.' },
    { icon: <Lightbulb className="w-8 h-8" />, titleKey: 'donate_impact_research', desc: 'Support cutting-edge research in telecommunications and digital technology.' }
  ];

  return (
    <div className="flex flex-col bg-white">
      <PageHero 
        title={t('nav_donate', 'Support ESTAD')}
        description={t('donate_hero_desc', 'Your contribution helps us provide world-class education and research opportunities in Central Africa.')}
        breadcrumbs={[{ label: t('nav_donate', 'Donate'), href: '/donate' }]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-20 px-6 lg:px-12 flex flex-col lg:flex-row gap-16">
        <Sidebar variant="admissions" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero image */}
          <div className="w-full h-56 md:h-80 overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold">
            <img src="/images/campus/ceremony-01.jpg" alt="ESTAD Community" className="w-full h-full object-cover object-top" />
          </div>

          {/* Your Impact Section */}
          <section id="impact" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Heart className="w-8 h-8 text-gold" /> {t('donate_impact_title', 'Your Impact')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {impactAreas.map((area, idx) => (
                <div key={idx} className="flex flex-col items-center text-center space-y-6 p-8 bg-gray-50 rounded-sm border border-gray-100 hover:shadow-xl transition-all group">
                  <div className="w-16 h-16 rounded-full bg-white shadow-md flex items-center justify-center text-navy group-hover:bg-navy group-hover:text-white transition-all duration-500">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-black uppercase tracking-tighter text-navy">{t(area.titleKey, area.titleKey)}</h3>
                  <p className="text-gray-500 text-sm font-medium leading-relaxed">{area.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Giving Options Section */}
          <section id="giving" className="bg-navy p-12 md:p-16 text-white rounded-sm shadow-2xl relative overflow-hidden group">
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold/5 rounded-full translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform duration-1000"></div>
            <div className="relative z-10 space-y-12">
               <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-white/10 pb-8">
                 <div className="space-y-4">
                   <span className="text-gold text-[10px] font-black uppercase tracking-[0.3em]">{t('donate_cta_badge', 'Make a Difference')}</span>
                   <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic italic leading-none">
                    {t('donate_giving_title', 'Façons de Donner')}
                  </h2>
                 </div>
                 <button className="bg-gold text-navy px-12 py-5 font-black uppercase tracking-widest text-sm shadow-xl hover:brightness-110 active:scale-95 transition-all flex items-center gap-3">
                   Donate Online <Wallet className="w-5 h-5" />
                 </button>
               </div>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-12 pt-4">
                 {[
                   { title: 'Corporate Partnerships', desc: 'Partner with ESTAD to support education and research while building your brand.', email: 'partnerships@estad.edu' },
                   { title: 'Planned Giving', desc: 'Include ESTAD in your estate planning to leave a lasting legacy in Africa.', email: 'development@estad.edu' }
                 ].map((opt, idx) => (
                   <div key={idx} className="space-y-4 border-l-4 border-gold pl-8 py-2">
                     <h4 className="text-2xl font-black uppercase tracking-tight text-white">{opt.title}</h4>
                     <p className="text-white/60 font-medium leading-relaxed">{opt.desc}</p>
                     <a href={`mailto:${opt.email}`} className="text-gold font-black underline hover:text-white transition-colors text-xs uppercase tracking-widest">{opt.email}</a>
                   </div>
                 ))}
               </div>
            </div>
          </section>

          {/* Recognition Banner */}
          <section id="recognition" className="border-2 border-navy/5 p-10 md:p-16 rounded-sm bg-offWhite flex flex-col md:flex-row gap-12 items-center shadow-inner relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-2 h-full bg-gold"></div>
            <div className="w-32 h-32 rounded-full bg-navy flex items-center justify-center flex-shrink-0 shadow-2xl border-4 border-gold/30 group-hover:scale-105 transition-transform">
               <Heart className="w-16 h-16 text-gold fill-gold" />
            </div>
            <div className="space-y-6">
              <h3 className="text-3xl font-black text-navy uppercase tracking-tighter italic leading-none">{t('donate_thanks_title', 'Thank You to Our Donors')}</h3>
              <p className="text-gray-600 text-lg leading-relaxed font-medium max-w-2xl">
                Your generosity enables the next generation of technology leaders in Central Africa. Every contribution, regardless of size, makes a meaningful difference.
              </p>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
